import React, { useEffect, useRef, useState } from 'react';
import { showErrorToast, showSuccessToast, showWarningToast } from 'features/toastSlice';
import LinkItem from 'components/LinkItem';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  setIsBraincareUser,
  setOrganizations,
  updateOrganizations,
  updateProfile,
} from 'features/userSlice';
import { directLogin, verifyTwoFactorLogin } from 'api/portal';
import { getToken } from 'utils/tokens';
import { resolveTwoFactorApiError } from 'utils/resolveTwoFactorApiError';
import {
  buildTwoFactorChallengeFromResponse,
  mergeTwoFactorChallengeState,
} from 'utils/twoFactorSkipPolicy';
import {
  clearPendingPostLoginRedirect,
  getPendingPostLoginRedirect,
  parseOrgIdFromRelativePath,
  persistPostLoginRedirect,
  tryNavigateToPendingPostLoginRedirect,
} from 'utils/postLoginRedirect';
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import UpdatePasswordForm from 'components/forms/UpdatePasswordForm';
import { useTranslation } from 'react-i18next';
import AccountList from 'components/AccountList';
import LoginForm from 'components/forms/LoginForm';
import TwoFactorLoginForm from 'components/forms/TwoFactorLoginForm';
import Button from 'components/Button';
import LoginDialog from 'components/dialogs/LoginDialog';
import { useTwoFactorResendCooldown } from 'hooks/useTwoFactorResendCooldown';
import {
  hasStoredSessionTokens,
  isMemberPendingOrgMisaligned,
  mapOrganizationsToAccountsWithLoginStatus,
  resolveEmailLinkAccountHint,
} from './directLoginRedirectSession';
import { completeCorporateLogin } from '../completeCorporateLogin';
import Timeout from '../../../components/routes/timeout';

function DirectLogin() {
  const [profile, setProfile] = useState(undefined);
  const [credentials, setCredentials] = useState({
    document: '',
    password: '',
    orgId: undefined,
    passwordUpdateToken: null,
  });

  const [accounts, setAccounts] = useState(undefined);
  const [account, setAccount] = useState(undefined);
  const [errors, setErrors] = useState(new Map());

  const [passwordUpdateType, setPasswordUpdateType] = useState(undefined);
  const [passwordPolicy, setPasswordPolicy] = useState({});
  const [skipSettings, setSkipSettings] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pendingTwoFactor, setPendingTwoFactor] = useState(null);
  const [twoFactorErrors, setTwoFactorErrors] = useState(new Map());
  const [isResendingTwoFactor, setIsResendingTwoFactor] = useState(false);

  const isTwoFactorActive = pendingTwoFactor !== null;
  const {
    canResend: canResendTwoFactor,
    secondsLeft: twoFactorResendSecondsLeft,
    restartCooldown: restartTwoFactorResendCooldown,
  } = useTwoFactorResendCooldown(isTwoFactorActive);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const ssoDeniedToastShownRef = useRef(false);
  const activeOrganizationId = useSelector((s) => s.user.active_organization?.id);
  const isBraincareUserFlag = useSelector((s) => s.user.isBraincareUser);
  const organizationsFromStore = useSelector((s) => s.user.organizations);
  const profileFromStore = useSelector((s) => s.user.profile);

  const { t } = useTranslation();

  const pendingLoginCredentialsRef = useRef({ document: '', password: '' });
  const emailLinkAccountHintHandledRef = useRef(false);
  const fetchOrgsForMisalignedDeepLinkRef = useRef(false);
  /** One-shot: after org list loads for a misaligned email deep link, open password for the link's org. */
  const deepLinkMisalignedAutoPasswordOpenedRef = useRef(false);
  const deepLinkLastPendingPathForAutoOpenRef = useRef(null);
  /** Org whose password modal was closed for discard-redirect dialog; reopen password on "Go back". */
  const accountToReopenAfterDiscardGoBackRef = useRef(null);
  /**
   * When onLoginSuccess consumes the redirect (snapshot + navigate), sessionStorage is cleared;
   * openSession effect may also call tryNavigateToPendingPostLoginRedirect when deps change.
   */
  const postLoginRedirectConsumedRef = useRef(false);
  /** After onLoginSuccess navigates by role / pending, skip the open-session effect's fallback `navigate('/')`. */
  const skipNextOpenSessionHomeRedirectRef = useRef(false);
  /**
   * `dispatch(setIsBraincareUser(true))` re-runs this effect; a queued microtask can then see an already
   * consumed pending redirect and must not fall through to `navigate('/')` (that cancelled /braincare/reports).
   */
  const openSessionPendingRedirectNavigatedRef = useRef(false);
  /** Bumps after clearing session deep-link so the account list remounts cleanly. */
  const [deepLinkUiEpoch, setDeepLinkUiEpoch] = useState(0);
  const [discardRedirectConfirmOpen, setDiscardRedirectConfirmOpen] = useState(false);
  /** When set, discard dialog opened after closing password modal while an email redirect was pending. */
  const [discardRedirectReason, setDiscardRedirectReason] = useState(null);

  /** Same sources as Menu corporate login (`user.profile.document`). */
  const getDocumentForDirectLogin = () => {
    const pick = (v) => String(v ?? '').trim();
    return pick(credentials.document) || pick(profile?.document) || pick(profileFromStore?.document);
  };

  useEffect(() => {
    if (ssoDeniedToastShownRef.current || searchParams.get('sso_denied') !== '1') {
      return;
    }
    ssoDeniedToastShownRef.current = true;
    dispatch(showErrorToast('auth.login.sso-denied'));
    const next = new URLSearchParams(searchParams);
    next.delete('sso_denied');
    setSearchParams(next, { replace: true });
  }, [dispatch, searchParams, setSearchParams]);

  useEffect(() => {
    const access = getToken('access_token');
    const refresh = getToken('refresh_token');
    if (!hasStoredSessionTokens(access, refresh)) {
      return undefined;
    }
    if (!getPendingPostLoginRedirect()) {
      return undefined;
    }
    dispatch(updateProfile());
    return undefined;
  }, [dispatch]);

  useEffect(() => {
    const raw = searchParams.get('redirect');
    if (raw == null) {
      return;
    }
    persistPostLoginRedirect(raw);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('redirect');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  /** Open login with tokens: apply pending redirect if allowed; if org mismatches email link, stay on picker. */
  useEffect(() => {
    const accessToken = getToken('access_token');
    const refreshToken = getToken('refresh_token');
    const hasStoredTokens = hasStoredSessionTokens(accessToken, refreshToken);

    const pendingPath = getPendingPostLoginRedirect();
    const pendingMisalignedOrg = isMemberPendingOrgMisaligned({
      pendingPath,
      activeOrganizationId,
    });

    if (!hasStoredTokens) {
      postLoginRedirectConsumedRef.current = false;
      skipNextOpenSessionHomeRedirectRef.current = false;
      openSessionPendingRedirectNavigatedRef.current = false;
      return undefined;
    }

    if (!pendingPath) {
      openSessionPendingRedirectNavigatedRef.current = false;
    }

    if (pendingMisalignedOrg) {
      openSessionPendingRedirectNavigatedRef.current = false;
      return undefined;
    }

    const bcPermsLenOpen = profileFromStore?.braincare_permissions?.length ?? 0;
    const hasBcAdminPermsOpen = bcPermsLenOpen > 0;
    const shouldAlignBcShellOpen =
      Boolean(pendingPath) && hasBcAdminPermsOpen && !isBraincareUserFlag;

    if (shouldAlignBcShellOpen) {
      dispatch(setIsBraincareUser(true));
    }

    const applyPendingOrHome = () => {
      const navigatedPending = tryNavigateToPendingPostLoginRedirect(navigate, {
        isBraincareUser: hasBcAdminPermsOpen || isBraincareUserFlag,
      });
      if (navigatedPending) {
        openSessionPendingRedirectNavigatedRef.current = true;
        return;
      }
      if (openSessionPendingRedirectNavigatedRef.current) {
        return;
      }
      if (postLoginRedirectConsumedRef.current) {
        return;
      }
      if (skipNextOpenSessionHomeRedirectRef.current) {
        skipNextOpenSessionHomeRedirectRef.current = false;
        return;
      }
      navigate('/', { replace: true });
    };

    if (shouldAlignBcShellOpen) {
      queueMicrotask(applyPendingOrHome);
      return undefined;
    }

    applyPendingOrHome();
    return undefined;
  }, [
    dispatch,
    navigate,
    activeOrganizationId,
    isBraincareUserFlag,
    profileFromStore?.braincare_permissions?.length,
  ]);

  useEffect(() => {
    const accessToken = getToken('access_token');
    const refreshToken = getToken('refresh_token');
    const hasStoredTokens = hasStoredSessionTokens(accessToken, refreshToken);

    if (!hasStoredTokens) {
      return undefined;
    }

    const pendingPath = getPendingPostLoginRedirect();
    const pendingMisalignedOrg = isMemberPendingOrgMisaligned({
      pendingPath,
      activeOrganizationId,
    });

    if (!pendingMisalignedOrg) {
      fetchOrgsForMisalignedDeepLinkRef.current = false;
      deepLinkMisalignedAutoPasswordOpenedRef.current = false;
      deepLinkLastPendingPathForAutoOpenRef.current = null;
      return undefined;
    }

    if (pendingPath !== deepLinkLastPendingPathForAutoOpenRef.current) {
      deepLinkMisalignedAutoPasswordOpenedRef.current = false;
      deepLinkLastPendingPathForAutoOpenRef.current = pendingPath;
    }

    if (!organizationsFromStore?.length) {
      if (!fetchOrgsForMisalignedDeepLinkRef.current) {
        fetchOrgsForMisalignedDeepLinkRef.current = true;
        dispatch(updateOrganizations());
      }
      return undefined;
    }

    fetchOrgsForMisalignedDeepLinkRef.current = false;
    const mapped = mapOrganizationsToAccountsWithLoginStatus(
      organizationsFromStore,
      activeOrganizationId,
      accessToken,
    );
    setAccounts(mapped);
    setProfile((prev) => (prev != null ? prev : { ...profileFromStore }));

    const wantPk = parseOrgIdFromRelativePath(pendingPath);
    const targetAccount =
      wantPk != null ? mapped.find((a) => String(a.id) === String(wantPk)) : undefined;
    if (
      targetAccount &&
      targetAccount.login_status === false &&
      !deepLinkMisalignedAutoPasswordOpenedRef.current
    ) {
      deepLinkMisalignedAutoPasswordOpenedRef.current = true;
      queueMicrotask(() => {
        setAccount(targetAccount);
      });
    }

    return undefined;
  }, [
    activeOrganizationId,
    dispatch,
    isBraincareUserFlag,
    organizationsFromStore,
    profileFromStore,
  ]);

  /**
   * Deep link com org errada: o utilizador chega com sessão e escolhe outra org no diálogo de palavra-passe.
   * `credentials.document` só era preenchido pelo LoginForm inicial — vinha vazio e o POST /direct-login falhava.
   */
  useEffect(() => {
    const accessToken = getToken('access_token');
    const refreshToken = getToken('refresh_token');
    const hasStoredTokens = hasStoredSessionTokens(accessToken, refreshToken);
    if (!hasStoredTokens) {
      return undefined;
    }

    const pendingPath = getPendingPostLoginRedirect();
    const pendingMisalignedOrg = isMemberPendingOrgMisaligned({
      pendingPath,
      activeOrganizationId,
    });

    if (!pendingMisalignedOrg) {
      return undefined;
    }

    const doc = getDocumentForDirectLogin();
    if (!doc) {
      return undefined;
    }

    setCredentials((prev) => {
      if (String(prev.document || '').trim() !== '') {
        return prev;
      }
      const nextDoc = doc;
      pendingLoginCredentialsRef.current = {
        ...pendingLoginCredentialsRef.current,
        document: nextDoc,
      };
      return { ...prev, document: nextDoc };
    });

    return undefined;
  }, [activeOrganizationId, isBraincareUserFlag, profileFromStore?.document, profile?.document, credentials.document]);

  const INVALID_CREDENTIAL = t('auth.login.auth-error');

  const isTwoFactorChallenge = (data) => Boolean(data?.two_factor);
  const isTwoFactorBlocked = (data) => Boolean(data?.two_factor_blocked);

  const handleLoginErrors = (data, requestBody) => {
    if (isTwoFactorChallenge(data)) {
      return null;
    }

    if (data.hasOwnProperty('update_password')) {
      setPasswordUpdateType(data.update_password);
      setCredentials({
        ...requestBody,
        orgId: data.org_id,
        passwordUpdateToken: data.password_update_token || null,
      });
      setPasswordPolicy(data.password_policy);
      setSkipSettings(data.skip_settings);
      return null;
    }

    if (data.hasOwnProperty('blocked')) {
      return 'auth.login.user-blocked';
    }

    if (data.hasOwnProperty('ip_blocked')) {
      return 'auth.login.ip-blocked';
    }

    if (data !== false) {
      return 'auth.login.auth-error';
    }

    return null;
  };

  const beginTwoFactor = (payload) => {
    setPasswordUpdateType(undefined);
    setPendingTwoFactor(payload);
    setTwoFactorErrors(new Map());
  };

  const onLoginSuccess = (data) => {
    skipNextOpenSessionHomeRedirectRef.current = true;
    setAccount(undefined);
    setProfile(data.user);
    const consumedPendingRedirect = completeCorporateLogin({ data, navigate, dispatch, t });
    if (consumedPendingRedirect) {
      postLoginRedirectConsumedRef.current = true;
    }
  };

  const onSubmitLoginForm = (formData) => {
    emailLinkAccountHintHandledRef.current = false;
    setIsSubmitting(true);

    const requestBody = {
      document: formData.document,
      password: formData.password,
    };

    directLogin(requestBody)
      .then((response) => {
        const { data } = response;
        const { accounts } = data;

        setIsSubmitting(false);

        dispatch(setOrganizations(accounts));

        if (data.access_token && data.refresh_token) {
          onLoginSuccess(data);
        } else {
          const { user } = data;

          pendingLoginCredentialsRef.current = {
            document: formData.document,
            password: formData.password,
          };
          setProfile(user ?? (data.first_name != null ? { first_name: data.first_name } : undefined));
          setCredentials({
            document: formData.document,
            password: formData.password,
            orgId: undefined,
            passwordUpdateToken: null,
          });
          setAccounts(accounts);
        }
      })
      .catch((error) => {
        const { response } = error;

        if (!response) {
          setIsSubmitting(false);
          dispatch(showErrorToast('auth.login.auth-error'));
          return;
        }

        const { data } = response;

        setIsSubmitting(false);

        if (isTwoFactorChallenge(data)) {
          if (Array.isArray(data.accounts)) {
            dispatch(setOrganizations(data.accounts));
          }
          beginTwoFactor(
            buildTwoFactorChallengeFromResponse(data, {
              document: requestBody.document,
              password: requestBody.password,
              orgId: data.org_id,
              restoreAccount: null,
            }),
          );
          return;
        }

        const errorMessage = handleLoginErrors(data, requestBody);

        if (errorMessage !== null) {
          dispatch(showErrorToast(errorMessage));
          return;
        }

        if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'update_password')) {
          return;
        }

        dispatch(showErrorToast('auth.login.auth-error'));
      });
  };

  const directLoginWithAccount = (document, password, account) => {
    const requestBody = {
      document,
      password,
      account: account.id,
    };

    directLogin(requestBody)
      .then((response) => {
        const { data } = response;

        setIsSubmitting(false);
        setIsLoading(false);

        if (data.access_token && data.refresh_token) {
          onLoginSuccess(data);
        } else {
          const { user } = data;
          setProfile(user);
        }
      })
      .catch((error) => {
        const { response } = error;

        setIsSubmitting(false);
        setIsLoading(false);

        if (!response) {
          setErrors(new Map([['password', INVALID_CREDENTIAL]]));
          return;
        }

        const { data } = response;

        if (isTwoFactorChallenge(data)) {
          beginTwoFactor(
            buildTwoFactorChallengeFromResponse(data, {
              document,
              password,
              orgId: data.org_id,
              restoreAccount: account,
            }),
          );
          setAccount(undefined);
          return;
        }

        const errorMessage = handleLoginErrors(data, requestBody);

        if (errorMessage !== null) {
          dispatch(showErrorToast(errorMessage));
          setErrors(new Map([['password', t(errorMessage)]]));
          return;
        }

        if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'update_password')) {
          return;
        }

        setErrors(new Map([['password', INVALID_CREDENTIAL]]));
      });
  };

  useEffect(() => {
    if (!accounts?.length) {
      return;
    }
    if (emailLinkAccountHintHandledRef.current) {
      return;
    }

    const pendingForAccounts = getPendingPostLoginRedirect();
    const hint = resolveEmailLinkAccountHint({
      pendingPath: pendingForAccounts,
      accounts,
      pendingCredentials: pendingLoginCredentialsRef.current,
    });

    if (hint.kind === 'noop') {
      return;
    }

    if (hint.kind === 'invalid-link') {
      emailLinkAccountHintHandledRef.current = true;
      clearPendingPostLoginRedirect();
      dispatch(showErrorToast('auth.login.email-link-invalid'));
      return;
    }

    if (hint.kind === 'handled-wait-password' || hint.kind === 'handled-missing-credentials') {
      emailLinkAccountHintHandledRef.current = true;
      return;
    }

    emailLinkAccountHintHandledRef.current = true;
    setIsLoading(true);
    setIsSubmitting(true);
    const docFromRef = String(pendingLoginCredentialsRef.current.document || '').trim();
    const document = docFromRef || getDocumentForDirectLogin();
    directLoginWithAccount(
      document,
      pendingLoginCredentialsRef.current.password,
      hint.account,
    );
  }, [accounts, dispatch]);

  const onSubmitLoginDialog = (formData) => {
    setIsSubmitting(true);
    directLoginWithAccount(getDocumentForDirectLogin(), formData.password, account);
  };

  const handleCancelTwoFactor = () => {
    setPendingTwoFactor(null);
    setTwoFactorErrors(new Map());
    setIsSubmitting(false);
    setIsResendingTwoFactor(false);
  };

  const handleTwoFactorExpired = () => {
    dispatch(showWarningToast(t('auth.login.two-factor-code-expired-toast')));
    handleCancelTwoFactor();
  };

  const handleTwoFactorBlocked = () => {
    // dispatch(showErrorToast('auth.login.two-factor-blocked'));
    handleCancelTwoFactor();
    setAccounts(undefined);
    setAccount(undefined);
    setProfile(undefined);
    setErrors(new Map());
    setPasswordUpdateType(undefined);
    setPasswordPolicy({});
    setSkipSettings(null);
    setCredentials({
      document: '',
      password: '',
      orgId: undefined,
      passwordUpdateToken: null,
    });
    navigate('/auth/direct-login');
  };

  const handleResendTwoFactor = () => {
    if (!pendingTwoFactor || !canResendTwoFactor || isResendingTwoFactor) {
      return;
    }

    const requestBody = {
      document: pendingTwoFactor.document,
      password: pendingTwoFactor.password,
      ...(pendingTwoFactor.orgId != null ? { account: pendingTwoFactor.orgId } : {}),
    };

    setIsResendingTwoFactor(true);

    const applyTwoFactorResendResponse = (data) => {
      if (!isTwoFactorChallenge(data)) {
        return false;
      }
      setPendingTwoFactor((prev) => (prev ? mergeTwoFactorChallengeState(prev, data) : prev));
      setTwoFactorErrors(new Map());
      restartTwoFactorResendCooldown();
      dispatch(showSuccessToast('auth.login.two-factor-resend-success'));
      return true;
    };

    directLogin(requestBody)
      .then((response) => {
        setIsResendingTwoFactor(false);
        const { data } = response;
        if (data?.access_token && data?.refresh_token) {
          setPendingTwoFactor(null);
          onLoginSuccess(data);
          return;
        }

        dispatch(showErrorToast('auth.login.two-factor-resend-error'));
      })
      .catch((error) => {
        const { response } = error;
        setIsResendingTwoFactor(false);

        if (!response?.data) {
          dispatch(showErrorToast('auth.login.auth-error'));
          return;
        }

        const { data } = response;

        if (applyTwoFactorResendResponse(data)) {
          return;
        }

        if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'update_password')) {
          setPendingTwoFactor(null);
          setTwoFactorErrors(new Map());
          setCredentials({
            document: pendingTwoFactor.document,
            orgId: pendingTwoFactor.orgId,
            password: pendingTwoFactor.password,
            passwordUpdateToken: data.password_update_token || null,
          });
          setPasswordUpdateType(data.update_password);
          setPasswordPolicy(data.password_policy);
          setSkipSettings(data.skip_settings);
          return;
        }

        const errorMessage = handleLoginErrors(data, requestBody);
        if (errorMessage !== null) {
          dispatch(showErrorToast(errorMessage));
          return;
        }

        dispatch(showErrorToast('auth.login.two-factor-resend-error'));
      });
  };

  const submitTwoFactor = ({ twoFactorCode, remember2faDays }) => {
    const trimmed = (twoFactorCode || '').replace(/\D/g, '');
    if (!trimmed) {
      setTwoFactorErrors(new Map([['code', t('auth.login.two-factor-code-empty')]]));
      return;
    }

    setTwoFactorErrors(new Map());
    setIsSubmitting(true);

    verifyTwoFactorLogin({
      document: pendingTwoFactor.document,
      orgId: pendingTwoFactor.orgId,
      password: pendingTwoFactor.password,
      twoFactorCode: trimmed,
      challengeToken: pendingTwoFactor.challengeToken,
      remember2faDays,
    })
      .then((response) => {
        setIsSubmitting(false);
        const { data } = response;
        setPendingTwoFactor(null);
        onLoginSuccess(data);
      })
      .catch((error) => {
        setIsSubmitting(false);
        const { response } = error;
        if (isTwoFactorBlocked(response?.data)) {
          handleTwoFactorBlocked();
          return;
        }
        if (response?.data?.update_password) {
          const { data } = response;
          setPendingTwoFactor(null);
          setPasswordUpdateType(data.update_password);
          setCredentials({
            document: pendingTwoFactor.document,
            password: pendingTwoFactor.password,
            orgId: data.org_id,
            passwordUpdateToken: data.password_update_token || null,
          });
          setPasswordPolicy(data.password_policy);
          setSkipSettings(data.skip_settings);
          return;
        }
        if (response?.data) {
          setTwoFactorErrors(new Map([['code', resolveTwoFactorApiError(response.data, t)]]));
          return;
        }
        setTwoFactorErrors(new Map([['code', t('auth.login.two-factor-invalid')]]));
      });
  };

  const applyOrganizationSelection = (pickedAccount) => {
    const accessToken = getToken('access_token');
    const refreshToken = getToken('refresh_token');

    if (pickedAccount.login_status) {
      if (accessToken === undefined || refreshToken === undefined) {
        setIsLoading(true);
        directLoginWithAccount(getDocumentForDirectLogin(), credentials.password, pickedAccount);
      } else {
        const pendingForShortcut = getPendingPostLoginRedirect();
        const bcPermsLen =
          profileFromStore?.braincare_permissions?.length ??
          profile?.braincare_permissions?.length ??
          0;
        const hasBcAdminPerms = bcPermsLen > 0;
        const shouldAlignBcShell =
          Boolean(pendingForShortcut) && hasBcAdminPerms && !isBraincareUserFlag;
        const pendingOrgPk = parseOrgIdFromRelativePath(pendingForShortcut ?? '');
        const pickedAccountNotLinkTarget =
          Boolean(pendingForShortcut) &&
          pendingOrgPk != null &&
          String(pickedAccount.id) !== String(pendingOrgPk);

        // tryNavigateToPendingPostLoginRedirect clears sessionStorage without matching org_id to the card.
        // Deep link to org B + org A card (login_status) must not consume the pending redirect.
        if (pickedAccountNotLinkTarget && !shouldAlignBcShell) {
          return;
        }

        if (shouldAlignBcShell) {
          dispatch(setIsBraincareUser(true));
          queueMicrotask(() => {
            if (
              tryNavigateToPendingPostLoginRedirect(navigate, {
                isBraincareUser: hasBcAdminPerms || isBraincareUserFlag,
              })
            ) {
              return;
            }
            navigate('/');
          });
          return;
        }
        if (
          tryNavigateToPendingPostLoginRedirect(navigate, {
            isBraincareUser: isBraincareUserFlag || hasBcAdminPerms,
          })
        ) {
          return;
        }
        navigate('/');
      }
    } else {
      setAccount(pickedAccount);
    }
  };

  const onAccountSelected = (pickedAccount) => {
    applyOrganizationSelection(pickedAccount);
  };

  const cancelDiscardEmailRedirectDialog = () => {
    const resume = accountToReopenAfterDiscardGoBackRef.current;
    accountToReopenAfterDiscardGoBackRef.current = null;
    setDiscardRedirectConfirmOpen(false);
    setDiscardRedirectReason(null);
    if (resume != null) {
      queueMicrotask(() => {
        setAccount(resume);
      });
    }
  };

  const confirmDiscardEmailRedirectDialog = () => {
    accountToReopenAfterDiscardGoBackRef.current = null;
    if (discardRedirectReason === 'password-cancel-pending-redirect') {
      clearPendingPostLoginRedirect();
      setDeepLinkUiEpoch((e) => e + 1);
    }
    setDiscardRedirectConfirmOpen(false);
    setDiscardRedirectReason(null);
  };

  const handleLoginDialogClose = () => {
    const closingOrganization = account;
    const pendingPath = getPendingPostLoginRedirect();
    const shouldOfferDiscardRedirect =
      Boolean(pendingPath) &&
      closingOrganization != null &&
      parseOrgIdFromRelativePath(pendingPath) != null;
    setErrors(new Map());
    if (shouldOfferDiscardRedirect) {
      accountToReopenAfterDiscardGoBackRef.current = closingOrganization;
      setDiscardRedirectReason('password-cancel-pending-redirect');
      setDiscardRedirectConfirmOpen(true);
    }
    setAccount(undefined);
  };

  const getLoggoutText = () => (
    <Typography variant="body2" color="secondary" marginBottom="32px">
      {t('auth.accounts-start.text-account-not-user', { firstname: profile?.first_name ?? '' })}
      <LinkItem href="/auth/logout" className="">
        {t('auth.accounts-start.button-logout')}
      </LinkItem>
    </Typography>
  );

  const onSuccessPasswordUpdate = (data) => {
    const { accounts } = data;

    if (Array.isArray(accounts)) {
      dispatch(setOrganizations(accounts));
    }

    if (data.access_token && data.refresh_token) {
      onLoginSuccess(data);
    } else {
      const { user } = data;
      setProfile(user);
    }
  };

  if (pendingTwoFactor !== null) {
    return (
      <Timeout>
        <TwoFactorLoginForm
          key={pendingTwoFactor.codeExpiresAt}
          errors={twoFactorErrors}
          isSubmitting={isSubmitting}
          maskedEmail={pendingTwoFactor.maskedEmail}
          codeExpiresAt={pendingTwoFactor.codeExpiresAt}
          twoFactorSkipPolicy={pendingTwoFactor.twoFactorSkipPolicy}
          onBack={handleCancelTwoFactor}
          onExpired={handleTwoFactorExpired}
          onSubmit={submitTwoFactor}
          onResend={handleResendTwoFactor}
          canResend={canResendTwoFactor}
          isResending={isResendingTwoFactor}
          resendSecondsLeft={twoFactorResendSecondsLeft}
        />
      </Timeout>
    );
  }

  if (passwordUpdateType !== undefined) {
    return (
      <Timeout>
        <UpdatePasswordForm
          credentials={credentials}
          passwordPolicy={passwordPolicy}
          onSuccess={onSuccessPasswordUpdate}
          skipSettings={skipSettings}
          type={passwordUpdateType}
        />
      </Timeout>
    );
  }

  const bootstrapAccess = getToken('access_token');
  const bootstrapRefresh = getToken('refresh_token');
  const bootstrapPendingPath = getPendingPostLoginRedirect();
  const redirectParamPresent = searchParams.get('redirect') != null;
  const bootstrapMisaligned =
    Boolean(bootstrapPendingPath) &&
    isMemberPendingOrgMisaligned({
      pendingPath: bootstrapPendingPath,
      activeOrganizationId,
    });
  if (
    hasStoredSessionTokens(bootstrapAccess, bootstrapRefresh) &&
    bootstrapPendingPath &&
    accounts === undefined &&
    (redirectParamPresent || bootstrapMisaligned)
  ) {
    return (
      <Timeout>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress aria-busy="true" aria-label={t('messages.loading')} />
        </Box>
      </Timeout>
    );
  }

  if (accounts) {
    const pendingPathForPasswordHint = getPendingPostLoginRedirect();
    const showEmailLinkRedirectHintForModal =
      account != null &&
      pendingPathForPasswordHint != null &&
      parseOrgIdFromRelativePath(pendingPathForPasswordHint) != null;

    return (
      <Timeout>
        <AccountList
          key={deepLinkUiEpoch}
          profile={profile}
          accounts={accounts}
          isLoading={isLoading}
          onAccountSelected={(picked) => onAccountSelected(picked)}
          loggoutText={getLoggoutText()}
        />
        <LoginDialog
          organization={account}
          isOpen={account !== undefined}
          onClose={handleLoginDialogClose}
          isSubmitting={isSubmitting}
          onSubmit={(data) => onSubmitLoginDialog(data)}
          errors={errors}
          showEmailLinkRedirectHint={showEmailLinkRedirectHintForModal}
        />
        <Dialog
          open={discardRedirectConfirmOpen}
          onClose={cancelDiscardEmailRedirectDialog}
          data-testid="discard-email-redirect-dialog"
        >
          <DialogTitle>{t('auth.login.email-link-discard-redirect-title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="secondary">
              {t('auth.login.email-link-discard-redirect-body')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button variant="outlined" sx={{}} onClick={cancelDiscardEmailRedirectDialog}>
              {t('auth.login.email-link-discard-redirect-cancel')}
            </Button>
            <Button color="gradient" sx={{}} onClick={confirmDiscardEmailRedirectDialog}>
              {t('auth.login.email-link-discard-redirect-confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </Timeout>
    );
  }

  return <LoginForm isSubmitting={isSubmitting} onSubmit={onSubmitLoginForm} />;
}

export default DirectLogin;
